import { proxy } from 'valtio';
import { subscribeKey } from 'valtio/utils';

import { Deferrable } from '@canvas/deferrable';
import { type UserIdentity } from '@canvas/model/identity';
import { type GlyphLocale, parseGlyphLocale } from '@canvas/model/text';

export type UserStatePartial = {
  accountId?: string;
};

// The state of the current user's identity
export type UserState =
  | { status: 'pending'; identity: undefined }
  | { status: 'partial'; identity: UserStatePartial }
  | { status: 'succeeded'; identity: UserIdentity };

export const currentUserState = proxy<UserState>({
  status: 'pending',
  identity: undefined,
});

export const setAccountIdOptimistically = (accountId: string) => {
  if (currentUserState.status === 'pending' || currentUserState.status === 'partial') {
    currentUserState.status = 'partial';
    currentUserState.identity = { accountId };
  }
};

export const getAccountIdOrThrow = () => {
  const accountId = currentUserState.identity?.accountId;
  if (!accountId) {
    throw Error('account id missing');
  }

  return accountId;
};

const localeDeferrable = new Deferrable<UserIdentity>();
const fabricLocaleDeferrable = new Deferrable<GlyphLocale>();

/**
 * Returns a promise which resolves once the current user's full identity data is known.
 *
 * @param deferrable A deferrable object to use for caching the result. If not provided,
 * a singleton deferrable object is used.
 * @returns A promise that resolves to the current user's full identity data.
 */
export const getUserIdentity = (deferrable = localeDeferrable): Promise<UserIdentity> => {
  if (!deferrable.fulfilled) {
    if (currentUserState.status === 'succeeded') {
      deferrable.resolve(currentUserState.identity);
    } else {
      const unsubscribe = subscribeKey(currentUserState, 'status', () => {
        // Need to cast to UserState because TypeScript thinks that `status` has been
        // narrowed to 'partial'
        const currentUserStateLocal = currentUserState as UserState;
        if (currentUserStateLocal.status === 'succeeded') {
          unsubscribe();
          deferrable.resolve(currentUserStateLocal.identity);
        }
      });
    }
  }

  return deferrable.promise;
};

/**
 * Parses the user's provided raw locale into a flattened list of known glyph locales
 * ({@link GlyphLocale}) to the Fabric application. Used for text rendering and
 * font loading.
 * @param deferrable
 * @returns
 */
export const getCanvasGlyphLocale = async (
  deferrable = fabricLocaleDeferrable,
): Promise<GlyphLocale> => {
  if (!deferrable.fulfilled) {
    const { locale } = await getUserIdentity();
    deferrable.resolve(parseGlyphLocale(locale));
  }

  return deferrable.get();
};

export const setFabricLocale = (locale: GlyphLocale) => {
  fabricLocaleDeferrable.resolve(locale);
};
